/**
 * App Handoff Page Script
 * Phase 2B: Production pricing architecture.
 *
 * Flow:
 *   1. Read handoff token from URL.
 *   2. Fetch handoff payload from embedded app backend.
 *   3. Fetch product pricing metadata from /products/{handle}?view=pricing-api.
 *   4. Match selectedOptions against customVariants (same logic as global.js).
 *   5. Calculate _baseunitprice / _unitprice from matched priceList.
 *   6. Fallback to /prices API for custom-dimension customizeable-box/inserts-product.
 *   7. Build cart properties and POST to /cart/add.js.
 *   8. Redirect to /cart or /checkout.
 *
 * Pricing source of truth: Arka.com product metafield custom.variants,
 * exposed via the pricing-api alternate template.
 */

(function () {
  'use strict';

  // ─── Configuration ─────────────────────────────────────────────────────────

  function getResolveBaseUrl() {
    if (window.arkaAppHandoff && window.arkaAppHandoff.resolveBaseUrl) {
      return window.arkaAppHandoff.resolveBaseUrl;
    }
    throw new Error('window.arkaAppHandoff.resolveBaseUrl is not configured. Ensure sections/app-handoff.liquid is loaded.');
  }

  // ─── DOM Helpers ───────────────────────────────────────────────────────────

  var $loading = document.getElementById('handoff-loading');
  var $success = document.getElementById('handoff-success');
  var $error = document.getElementById('handoff-error');
  var $errorDetail = document.getElementById('handoff-error-detail');
  var $productLink = document.getElementById('handoff-product-link');

  function showLoading() {
    $loading.classList.remove('hidden');
    $success.classList.add('hidden');
    $error.classList.add('hidden');
  }

  function showSuccess() {
    $loading.classList.add('hidden');
    $success.classList.remove('hidden');
    $error.classList.add('hidden');
  }

  function showError(detail, productHandle) {
    $loading.classList.add('hidden');
    $success.classList.add('hidden');
    $error.classList.remove('hidden');

    if (detail) {
      $errorDetail.textContent = detail;
    }

    if (productHandle) {
      $productLink.href = '/products/' + productHandle;
      $productLink.classList.remove('hidden');
    }
  }

  // ─── Pricing Helpers (mirror global.js logic) ──────────────────────────────

  /**
   * Matches selectedOptions against customVariants array.
   * Mirrors global.js getCustomVariantByOptions (lines 558-581).
   *
   * - Lowercases all keys and values for comparison.
   * - Compares against the keys defined in each custom variant entry.
   * - Returns the first matching entry or null.
   */
  function getCustomVariantByOptions(customVariants, options) {
    if (!customVariants || !Array.isArray(customVariants)) return null;

    return customVariants.find(function (variant) {
      if (!variant || !variant.options) return false;

      var keys = Object.keys(variant.options);

      var allMatch = keys.every(function (key) {
        var optionKey = key.toLowerCase();
        var a = options[optionKey] != null ? String(options[optionKey]).toLowerCase() : null;
        var b = variant.options[key] != null ? String(variant.options[key]).toLowerCase() : null;
        return a === b;
      });

      return allMatch;
    }) || null;
  }

  /**
   * Selects the correct unit price from a priceList based on quantity.
   * Mirrors global.js getUnitPriceForQuantity (lines 583-608).
   *
   * For non-fixed quantity: returns the last tier where tier.quantity <= quantity.
   * For fixed quantity: returns exact match only.
   */
  function getUnitPriceForQuantity(priceList, quantity, fixedQuantity) {
    var volume = null;
    var foundVolume = false;

    if (!fixedQuantity) {
      for (var i = 0; i < priceList.length; i++) {
        if (parseInt(priceList[i].quantity) > parseInt(quantity)) {
          foundVolume = true;
          break;
        }
        volume = priceList[i];
      }

      if (!foundVolume && priceList.length > 0) {
        volume = priceList[priceList.length - 1];
        foundVolume = parseInt(volume.quantity) <= parseInt(quantity);
      }
    } else {
      for (var j = 0; j < priceList.length; j++) {
        if (parseInt(priceList[j].quantity) === parseInt(quantity)) {
          volume = priceList[j];
          foundVolume = true;
          break;
        }
      }
    }

    return volume && foundVolume ? volume.unitPrice : null;
  }

  /**
   * Applies surcharge multipliers to base unit price.
   * Mirrors global.js finalUnitPrice getter (lines 434-458).
   */
  function calculateFinalUnitPrice(baseUnitPrice, printing, production) {
    var price = baseUnitPrice;

    if (printing && printing.toLowerCase() === 'double sided') {
      price *= 2;
    }

    if (production && production.toLowerCase() === 'rush') {
      price *= 2;
    }

    return parseFloat((Math.round(price * 100) / 100).toFixed(2));
  }

  /**
   * Fetches the priceList from Arka internal pricing API.
   * Only used as fallback for customizeable-box/inserts-product with custom 3D dimensions.
   * GET /prices?length=L&width=W&height=H&variantId=ID
   */
  async function fetchPriceListFromApi(size, variantId) {
    if (!window.internalApi || !window.internalApi.baseUrl) {
      return null;
    }

    var parts = size.replace(/\s/g, '').split('x');
    if (parts.length < 3) return null;

    var url = new URL(window.internalApi.baseUrl + '/prices');
    url.searchParams.set('length', parts[0]);
    url.searchParams.set('width', parts[1]);
    url.searchParams.set('height', parts[2]);
    url.searchParams.set('variantId', variantId);

    var res = await fetch(url).then(function (r) {
      return r.ok ? r.json() : null;
    });

    return res && res.data ? res.data : null;
  }

  /**
   * Fetches product pricing metadata from the Arka.com pricing-api template.
   * GET /products/{handle}?view=pricing-api
   */
  async function fetchProductPricingData(productHandle) {
    var url = '/products/' + encodeURIComponent(productHandle) + '?view=pricing-api';

    // Do NOT send Accept: application/json — Shopify intercepts that header
    // and returns its built-in product JSON API instead of rendering the Liquid template.
    var response = await fetch(url);

    if (!response.ok) return null;

    // Shopify serves alternate templates as text/html, so response.json()
    // can fail or return empty on whitespace/BOM issues. Parse from text instead.
    var text = await response.text();
    var trimmed = text.trim();

    console.log('[app-handoff] pricing-api raw length:', text.length, 'trimmed length:', trimmed.length);

    try {
      var parsed = JSON.parse(trimmed);
      // Unwrap if Shopify wraps response under a top-level "product" key
      var data = parsed.product || parsed;
      console.log('[app-handoff] productData keys:', Object.keys(data));
      console.log('[app-handoff] productType parsed:', data.productType);
      console.log('[app-handoff] customVariants raw type:', typeof data.customVariants);
      console.log('[app-handoff] customVariants parsed count:', Array.isArray(data.customVariants) ? data.customVariants.length : 'not-array');
      return data;
    } catch (e) {
      console.error('[app-handoff] Failed to parse pricing-api response:', e.message);
      console.error('[app-handoff] First 200 chars:', trimmed.substring(0, 200));
      return null;
    }
  }

  /**
   * Normalizes selectedOptions keys to lowercase for matching.
   */
  function normalizeOptions(opts) {
    var normalized = {};
    for (var key in opts) {
      if (Object.prototype.hasOwnProperty.call(opts, key)) {
        normalized[key.toLowerCase()] = opts[key];
      }
    }
    return normalized;
  }

  /**
   * Resolves the correct Shopify variant ID from productData based on selectedOptions.
   * Uses productData.options to determine option order, then matches against variant.options array.
   * Falls back to provided fallbackVariantId if no match found.
   */
  function resolveVariantId(productData, selectedOptions, fallbackVariantId) {
    if (!productData || !productData.options || !productData.variants) {
      return fallbackVariantId;
    }

    // Build expected options array in position order
    var expectedOptions = productData.options.map(function (opt) {
      var key = opt.name.toLowerCase();
      var val = selectedOptions[key];
      return val != null ? String(val) : null;
    });

    // Find variant whose options match (case-insensitive, whitespace-normalized)
    var matched = productData.variants.find(function (variant) {
      if (!variant.options || variant.options.length !== expectedOptions.length) return false;

      return expectedOptions.every(function (expected, idx) {
        if (expected === null) return true; // skip options not in selectedOptions
        var a = expected.replace(/\s/g, '').toLowerCase();
        var b = String(variant.options[idx]).replace(/\s/g, '').toLowerCase();
        return a === b;
      });
    });

    if (matched) {
      console.log('[app-handoff] resolvedVariantId:', matched.id, 'title:', matched.title);
      return matched.id;
    }

    console.warn('[app-handoff] Could not resolve variant from selectedOptions, using fallback:', fallbackVariantId);
    return fallbackVariantId;
  }

  /**
   * Determines if a size string looks like a 3D custom dimension (LxWxH).
   */
  function isCustom3dSize(size) {
    if (!size) return false;
    var parts = size.replace(/\s/g, '').split('x');
    return parts.length >= 3 && parts.every(function (p) { return !isNaN(parseFloat(p)); });
  }

  /**
   * Determines if product type supports /prices API fallback.
   */
  function supportsCustomPricingApi(productType) {
    var type = (productType || '').toLowerCase();
    return type === 'customizeable-box' || type === 'inserts-product';
  }

  /**
   * Determines if product is a case-quantity product.
   */
  function isCaseQuantityProduct(tags) {
    if (!tags || !Array.isArray(tags)) return false;
    return tags.some(function (t) { return t.toLowerCase() === 'case-quantity'; });
  }

  // ─── Main Flow ─────────────────────────────────────────────────────────────

  async function run() {
    showLoading();

    // 1. Read handoff token from URL
    var params = new URLSearchParams(window.location.search);
    var token = params.get('handoff');

    if (!token) {
      showError('No handoff token provided.');
      return;
    }

    // 2. Fetch handoff payload from app backend
    var handoff;
    try {
      var resolveUrl = getResolveBaseUrl() + '/api/v1/external-handoffs/resolve/' + encodeURIComponent(token);
      var response = await fetch(resolveUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        showError('Handoff not found or expired. (HTTP ' + response.status + ')');
        return;
      }

      var json = await response.json();
      handoff = json.data;
    } catch (err) {
      showError('Could not connect to the Arka app. Please try again.');
      return;
    }

    // 3. Validate handoff payload
    if (!handoff) {
      showError('Invalid handoff response.');
      return;
    }

    if (handoff.status === 'expired' || handoff.status === 'cancelled') {
      showError('This handoff has expired or was cancelled.', handoff.productHandle);
      return;
    }

    if (handoff.expiresAt && new Date(handoff.expiresAt) < new Date()) {
      showError('This handoff link has expired.', handoff.productHandle);
      return;
    }

    if (!handoff.variantId || !handoff.quantity) {
      showError('Handoff is missing required product data.', handoff.productHandle);
      return;
    }

    if (!handoff.selectedOptions) {
      showError('Handoff is missing product options.', handoff.productHandle);
      return;
    }

    // 4. Check action type
    if (handoff.action === 'cloudlab') {
      // Phase 1: Store context and redirect to product page for manual CloudLab launch.
      var handoffContext = {
        handoffId: handoff.handoffId,
        productHandle: handoff.productHandle,
        variantId: handoff.variantId,
        quantity: handoff.quantity,
        selectedOptions: handoff.selectedOptions || {},
        cartAttributes: handoff.cartAttributes || {},
        source: 'arka_app_handoff',
        createdAt: Date.now(),
        expiresAt: Date.now() + (24 * 60 * 60 * 1000),
      };

      try {
        localStorage.setItem('appHandoff_' + handoff.handoffId, JSON.stringify(handoffContext));
        console.log('[app-handoff] cloudlab context stored: appHandoff_' + handoff.handoffId);
      } catch (e) {
        console.warn('[app-handoff] Could not write to localStorage:', e.message);
      }

      var productUrl = '/products/' + encodeURIComponent(handoff.productHandle)
        + '?app_handoff=' + encodeURIComponent(handoff.handoffId);

      console.log('[app-handoff] cloudlab redirect URL:', productUrl);

      showSuccess();
      setTimeout(function () {
        window.location.href = productUrl;
      }, 300);
      return;
    }

    if (handoff.action !== 'add_to_cart') {
      showError('This handoff type is not supported yet.', handoff.productHandle);
      return;
    }

    // 5. Fetch product pricing metadata from Arka.com
    var productData;
    try {
      productData = await fetchProductPricingData(handoff.productHandle);
    } catch (err) {
      productData = null;
    }

    if (!productData) {
      showError('Could not load product pricing data.', handoff.productHandle);
      return;
    }

    // 5b. Defensive: parse customVariants if it came back as a string
    if (typeof productData.customVariants === 'string') {
      try {
        productData.customVariants = JSON.parse(productData.customVariants);
      } catch (e) {
        console.error('[app-handoff] Failed to parse customVariants string:', e);
        productData.customVariants = [];
      }
    }

    // 6. Normalize options and calculate pricing
    var opts = normalizeOptions(handoff.selectedOptions);

    // Resolve correct Shopify variant from selectedOptions (handoff.variantId may be stale/default)
    var fallbackVariantId = parseInt(handoff.variantId, 10);
    var variantId = resolveVariantId(productData, opts, fallbackVariantId);
    console.log('[app-handoff] handoff.variantId:', handoff.variantId, '→ resolved variantId:', variantId);

    if (!variantId || isNaN(parseInt(variantId, 10))) {
      showError('Invalid variant ID.', handoff.productHandle);
      return;
    }
    variantId = parseInt(variantId, 10);

    var quantity = parseInt(handoff.quantity, 10);
    var productType = productData.productType || '';
    var tags = productData.tags || [];
    var customVariants = productData.customVariants || [];
    var isCaseQty = isCaseQuantityProduct(tags);

    // For case-quantity package products, calculate actual unit quantity.
    // Package value × max quantity from priceList = actual units.
    var actualQuantity = quantity;
    var caseQuantityValue = null;

    if (isCaseQty && opts.package) {
      // For package products, we need to find the matched variant first
      // to determine the per-package unit count (maxQuantity from last priceList entry).
      // We'll calculate actualQuantity after matching.
    }

    // Build currentOptions for matching.
    // For customizeable-box/inserts-product, the matching uses: size, material, printing.
    // For other product types, matching uses all keys from the custom variant entry.
    var currentOptions;

    if (supportsCustomPricingApi(productType)) {
      currentOptions = {};
      if (opts.size) currentOptions.size = opts.size;
      if (opts.material) currentOptions.material = opts.material;
      currentOptions.printing = opts.printing || 'Outside';
    } else {
      // For extension products: use all selectedOptions as-is (minus qty/package for lookup)
      currentOptions = {};
      for (var key in opts) {
        if (Object.prototype.hasOwnProperty.call(opts, key)) {
          if (key !== 'qty' && key !== 'package' && key !== 'production') {
            currentOptions[key] = opts[key];
          }
        }
      }
    }

    // 7. Match against customVariants
    console.log('[app-handoff] currentOptions for matching:', JSON.stringify(currentOptions));
    console.log('[app-handoff] customVariants count:', customVariants.length);

    var matchedVariant = getCustomVariantByOptions(customVariants, currentOptions);
    var priceList = null;
    var usedApiFallback = false;

    console.log('[app-handoff] matchedVariant:', matchedVariant ? 'FOUND' : 'NULL');

    if (matchedVariant && matchedVariant.priceList && matchedVariant.priceList.length > 0) {
      priceList = matchedVariant.priceList;
    } else if (supportsCustomPricingApi(productType) && opts.size && isCustom3dSize(opts.size)) {
      // Fallback: custom 3D dimensions for customizeable-box/inserts-product
      try {
        priceList = await fetchPriceListFromApi(opts.size, variantId);
        usedApiFallback = true;
      } catch (err) {
        priceList = null;
      }
    }

    if (!priceList || priceList.length === 0) {
      console.error('[app-handoff] No priceList found. matchedVariant:', matchedVariant);
      console.error('[app-handoff] productType:', productType, 'opts.size:', opts.size);
      showError(
        'Pricing is unavailable for this product configuration. Please try ordering directly on the product page.',
        handoff.productHandle
      );
      return;
    }

    // 8. Handle case-quantity package logic
    // For case-quantity products, the handoff quantity represents package count.
    // Actual unit quantity = packageCount × unitsPerPackage (last priceList tier quantity).
    if (isCaseQty) {
      var lastTier = priceList[priceList.length - 1];
      var unitsPerPackage = parseInt(lastTier.fakeQty || lastTier.quantity, 10);
      var packageCount = opts.package ? parseInt(opts.package, 10) || 1 : quantity;

      caseQuantityValue = unitsPerPackage;
      actualQuantity = packageCount * unitsPerPackage;

      console.log('[app-handoff] case-quantity detected');
      console.log('[app-handoff] raw handoff.quantity:', quantity);
      console.log('[app-handoff] opts.package:', opts.package);
      console.log('[app-handoff] unitsPerPackage (from last tier):', unitsPerPackage);
      console.log('[app-handoff] packageCount:', packageCount);
      console.log('[app-handoff] actualQuantity:', actualQuantity);
      console.log('[app-handoff] _caseQuantity:', caseQuantityValue);
    }

    // Determine if this is a fixed-quantity product.
    // Per global.js line 351: isCaseQuantity products always use NON-fixed lookup.
    // Fixed quantity only applies to non-case-quantity products with non-editable dropdown.
    var isFixedQuantity = false;

    console.log('[app-handoff] isFixedQuantity:', isFixedQuantity, 'actualQuantity for lookup:', actualQuantity);
    console.log('[app-handoff] priceList tiers:', JSON.stringify(priceList));

    // 9. Get unit price from priceList
    var baseUnitPrice = getUnitPriceForQuantity(priceList, actualQuantity, isFixedQuantity);
    console.log('[app-handoff] baseUnitPrice from tier lookup:', baseUnitPrice);

    if (baseUnitPrice === null || baseUnitPrice <= 0) {
      showError(
        'Could not determine pricing for this quantity. The selected quantity may not be available.',
        handoff.productHandle
      );
      return;
    }

    // 10. Build cart properties
    var properties = {
      _token: Date.now(),
      _appHandoffId: handoff.handoffId,
      _baseunitprice: baseUnitPrice,
      _unitprice: calculateFinalUnitPrice(baseUnitPrice, opts.printing, opts.production),
    };

    if (opts.size) {
      properties._customSize = opts.size;
      properties.SizeLabel = opts.size;
    }

    if (opts.printing) {
      properties._printing = opts.printing;
    }

    if (opts.production) {
      properties._production = opts.production;
    }

    if (caseQuantityValue) {
      properties._caseQuantity = caseQuantityValue;
    }

    // Sample flag for customizeable-box/inserts-product at qty 1
    if (supportsCustomPricingApi(productType) && actualQuantity === 1) {
      properties._sample = 'true';
    }

    // 11. Add to cart
    try {
      var cartBody = {
        items: [{
          id: variantId,
          quantity: actualQuantity,
          properties: properties,
        }],
      };

      console.log('[app-handoff] cart add payload:', JSON.stringify(cartBody));

      var cartResponse = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(cartBody),
      });

      var cartResult = await cartResponse.json();
      console.log('[app-handoff] cart add response:', cartResponse.status, JSON.stringify(cartResult).substring(0, 300));

      if (!cartResponse.ok || cartResult.status) {
        var cartErr = cartResult.description || cartResult.message || 'Unknown cart error';
        showError('Could not add item to cart: ' + cartErr, handoff.productHandle);
        return;
      }

      // Set cart-level attributes separately via /cart/update.js
      if (handoff.cartAttributes && Object.keys(handoff.cartAttributes).length > 0) {
        await fetch(routes.cart_update_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: handoff.cartAttributes }),
        });
      }
    } catch (err) {
      showError('Failed to add item to cart. Please try again.', handoff.productHandle);
      return;
    }

    // 12. Success — redirect to cart or checkout
    showSuccess();

    var redirectPath = '/cart';
    if (handoff.redirect === 'checkout') {
      redirectPath = '/checkout';
    }

    setTimeout(function () {
      window.location.href = redirectPath;
    }, 500);
  }

  // ─── Init ──────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
